import { AxiosRequestConfig, AxiosResponse } from 'axios';
import client from 'api/client';
import { AppDocStatus } from '../models/AppDocStatus';

/**
 * User-scoped document type filter. The backend currently exposes `USER` for
 * documents tied to a candidate (e.g. Passport, Resume, Proof of address) but
 * other values are accepted by the same endpoint, so we keep them typed.
 */
export type UserDocumentType = 'USER' | 'ORGANIZATION' | 'SERVICES_ALL' | 'SERVICES_SELECTED';

/**
 * Shape of a user document returned by GET /api/users/:user_id/documents.
 *
 * The endpoint returns an empty serialized object (`{}`) when no document is
 * attached to the requested template, which is why every field is optional —
 * callers check `resource_name` to determine whether a real document exists.
 */
export interface UserDocument {
    id?: number;
    user_id?: number;
    application_id?: number | null;
    document_template_id?: number;
    document_name?: string;
    resource_name?: string | null;
    status?: AppDocStatus;
    timestamp?: string | null;
    explanation?: string | null;
}

/**
 * Fetch a user-level document for a given DocumentTemplate.name_en.
 *
 * Returns the serialized document, or an empty object if the user has nothing
 * attached to that template yet. Caller must be the same user or admin.
 */
export function get(
    userId: string | number,
    nameEn?: string,
    documentType?: UserDocumentType,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<UserDocument>> {
    return client.get<UserDocument>(`/users/${userId}/documents`, {
        params: {
            ...(nameEn ? { name_en: nameEn } : {}),
            ...(documentType ? { document_type: documentType } : {}),
        },
        ...customConfig,
    });
}

/**
 * Upload (or replace) a user-level document attached to a DocumentTemplate
 * matching `nameEn`. The backend handles upsert + S3 cleanup of any previous
 * file and sets the status to `WAITING_FOR_APPROVAL`.
 */
export function post(
    userId: string | number,
    nameEn: string,
    file: File,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<UserDocument>> {
    const formData = new FormData();
    formData.append('name_en', nameEn);
    formData.append('file', file);
    return client.post<UserDocument>(`/users/${userId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        ...customConfig,
    });
}

/**
 * Resolve a (signed) resource URI for a user-level document so it can be
 * previewed inline. The endpoint returns `{ url: string }` — the same shape
 * the application-scoped `resource_uri` endpoints already use elsewhere in
 * the app.
 */
export function getResourceUri(
    userId: string | number,
    docId: string | number,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<{ url: string }>> {
    return client.get<{ url: string }>(
        `/users/${userId}/documents/${docId}/resource_uri`,
        customConfig
    );
}

/**
 * Copy an existing (user-level) document onto a specific application/case.
 *
 * The backend creates a new Document row with the same template, resource,
 * status, name and explanation, then sets `application_id` to the target
 * application. The underlying S3 object is reused — no file is re-uploaded.
 *
 * Returns the serialized copied document on success.
 */
export function copyToApplication(
    documentId: string | number,
    applicationId: string | number,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<UserDocument>> {
    return client.post<UserDocument>(
        `/documents/${documentId}/applications/${applicationId}`,
        undefined,
        customConfig
    );
}
